import type { UtilityBarProps } from '~/components/blocks/UtilityBar/types';
import { deepEqual } from '~/utils/jsonHelper';
import { collectUtilityBarBlocks } from './helpers/collect-utility-bar-blocks';

/**
 * Manages UtilityBar block data retrieval and syncs it to the global state store.
 * Defaults are applied exclusively by useUtilityBarState.setContent (single source of truth).
 * No mutations happen inside computeds — block data stays clean for dirty detection.
 */
export const useUtilityBarConfiguration = (uuid?: string) => {
  const { blockUuid } = useSiteConfiguration();
  const { allBlocks } = useBlocks();
  const { findOrDeleteBlockByUuid } = useBlockManager();

  const targetUuid = computed(() => uuid || blockUuid.value);
  const {
    content: stateContent,
    sections,
    setContent,
    paddingStyles,
    isSectionVisible,
    getSectionFlexOrder,
    isActionVisible,
    getActionOrder,
    isFullSearchMode,
  } = useUtilityBarState(targetUuid.value);

  const utilityBarBlock = computed<UtilityBarProps | null>(() => {
    if (targetUuid.value) {
      const blockByUuid = findOrDeleteBlockByUuid(allBlocks.value, targetUuid.value) as UtilityBarProps | null;
      if (blockByUuid) return blockByUuid;
    }

    const utilityBarMatches = collectUtilityBarBlocks(allBlocks.value);
    if (utilityBarMatches.length === 1) {
      return utilityBarMatches[0] || null;
    }

    return null;
  });

  watch(
    () => utilityBarBlock.value,
    (block) => {
      if (!block) {
        return;
      }

      if (!deepEqual(stateContent.value, block.content)) {
        setContent(block.content);
      }
    },
    { immediate: true },
  );

  watch(
    stateContent,
    (newContent) => {
      const block = utilityBarBlock.value;
      if (block && newContent) {
        if (deepEqual(block.content, newContent)) {
          return;
        }

        block.content = deepClone(newContent);
      }
    },
    { deep: true },
  );

  const content = computed<UtilityBarProps['content']>({
    get: () => stateContent.value,
    set: (newConfiguration) => {
      stateContent.value = newConfiguration;
    },
  });

  return {
    utilityBarBlock,
    content,
    sections,
    paddingStyles,
    isSectionVisible,
    getSectionFlexOrder,
    isActionVisible,
    getActionOrder,
    isFullSearchMode,
  };
};
